import { get, put } from "aws-amplify/api";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Form, InputGroup, Row, Table } from "react-bootstrap";
import Select from "react-select";
import "containers/common/List.css";
import "containers/common/Table.css";
import { createElementIdFactory } from "utils/elementIds";

function decorateContactMappings(contacts) {
  return (contacts || []).map((contact) => ({
    ...contact,
    map_to_entity_contact_id: -1,
    map_to_contact: "Copy across",
  }));
}

function EntityPageFindAndReplace({
  isAuthenticated,
  entityid,
  onClose,
}) {
  const createId = createElementIdFactory("entity-find-replace");
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [findEntityOptions, setFindEntityOptions] = useState([]);
  const [findEntityContactsOptions, setFindEntityContactsOptions] = useState([]);
  const [replaceEntityOptions, setReplaceEntityOptions] = useState([]);
  const [replaceEntityContactsOptions, setReplaceEntityContactsOptions] = useState([]);
  const [replaceEntitySearchString, setReplaceEntitySearchString] = useState("");
  const [selectedFindEntity, setSelectedFindEntity] = useState(null);
  const [findEntitySearchString, setFindEntitySearchString] = useState("");
  const [selectedReplaceEntity, setSelectedReplaceEntity] = useState(null);
  const [showFindEntityDropdown, setShowFindEntityDropdown] = useState(false);
  const [showReplaceEntityDropdown, setShowReplaceEntityDropdown] = useState(false);
  const [salesOption, setSalesOption] = useState(false);
  const [saleSheetsOption, setSaleSheetsOption] = useState(true);
  const [managingAgentOption, setManagingAgentOption] = useState(true);
  const [leasingAgentOption, setLeasingAgentOption] = useState(true);
  const [liftOperatorsOption, setLiftOperatorsOption] = useState(true);
  const [selectedScopes] = useState([]);

  const canSubmit = Boolean(selectedFindEntity && selectedReplaceEntity)
    && selectedFindEntity?.value !== selectedReplaceEntity?.value;

  const replaceContactSelectOptions = useMemo(
    () => [{ value: -1, label: "Copy across" }, ...replaceEntityContactsOptions],
    [replaceEntityContactsOptions]
  );

  async function readJsonResponse(operation) {
    const response = await operation.response;
    return await response.body.json();
  }

  const getEntities = useCallback(async (filter) => {
    const queryParams = Object.fromEntries(
      Object.entries(filter || {}).filter(([, value]) => value !== "" && value !== null && value !== undefined)
    );

    const response = await get({
      apiName: "colin",
      path: "/entities/dropdown",
      options: { queryParams },
    }).response;

    return await response.body.json();
  }, []);

  const getEntityContacts = useCallback(async (filter) => {
    const queryParams = Object.fromEntries(
      Object.entries(filter || {}).filter(([, value]) => value !== "" && value !== null && value !== undefined)
    );

    const response = await get({
      apiName: "colin",
      path: "/entitycontacts/dropdown",
      options: { queryParams },
    }).response;

    return await response.body.json();
  }, []);

  async function executeFindAndReplace(body) {
    return await readJsonResponse(
      put({
        apiName: "colin",
        path: "/entities/findandreplace",
        options: { body },
      })
    );
  }

  function clearMessages() {
    setErrorMessage("");
    setSuccessMessage("");
  }

  useEffect(() => {
    if (!isAuthenticated) {
      setIsLoading(false);
      return;
    }

    let isMounted = true;

    async function initialize() {
      setIsLoading(true);
      clearMessages();

      try {
        if (!entityid) {
          return;
        }

        const [initialEntityOptions, initialContacts] = await Promise.all([
          getEntities({ entityid, activeflag: true }),
          getEntityContacts({ entityid, activeflag: true }),
        ]);

        if (!isMounted) {
          return;
        }

        setFindEntityOptions(initialEntityOptions || []);
        setSelectedFindEntity(initialEntityOptions?.[0] ?? null);
        setFindEntityContactsOptions(decorateContactMappings(initialContacts));
      } catch (error) {
        console.error("Error initialising entity find and replace", error);
        if (isMounted) {
          setErrorMessage("Unable to load entity find and replace data.");
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    initialize();

    return () => {
      isMounted = false;
    };
  }, [entityid, getEntities, getEntityContacts, isAuthenticated]);

  async function handleFindEntitySearch() {
    clearMessages();
    setShowFindEntityDropdown(false);
    setSelectedFindEntity(null);
    setFindEntityContactsOptions([]);

    try {
      const options = await getEntities({ name: findEntitySearchString, activeflag: true });
      setFindEntityOptions(options || []);
      setShowFindEntityDropdown(true);
    } catch (error) {
      console.error("Error loading find entities", error);
      setErrorMessage("Error loading entities to find.");
    }
  }

  async function handleReplaceEntitySearch() {
    clearMessages();
    setShowReplaceEntityDropdown(false);

    try {
      const options = await getEntities({ name: replaceEntitySearchString });
      setReplaceEntityOptions(options || []);
      setShowReplaceEntityDropdown(true);
    } catch (error) {
      console.error("Error loading replacement entities", error);
      setErrorMessage("Error loading replacement entities.");
    }
  }

  async function handleFindEntityChange(option) {
    clearMessages();
    setSelectedFindEntity(option);

    if (!option) {
      setFindEntityContactsOptions([]);
      return;
    }

    try {
      const contacts = await getEntityContacts({ entityid: option.value, activeflag: true });
      setFindEntityContactsOptions(decorateContactMappings(contacts));
    } catch (error) {
      console.error("Error loading entity contacts", error);
      setFindEntityContactsOptions([]);
      setErrorMessage("Error loading contacts for the entity to find.");
    }
  }

  async function handleReplaceEntityChange(option) {
    clearMessages();
    setSelectedReplaceEntity(option);
    setFindEntityContactsOptions((currentContacts) => decorateContactMappings(currentContacts));

    if (!option) {
      setReplaceEntityContactsOptions([]);
      return;
    }

    try {
      const contacts = await getEntityContacts({ entityid: option.value, activeflag: true });
      setReplaceEntityContactsOptions(contacts || []);
    } catch (error) {
      console.error("Error loading replacement entity contacts", error);
      setReplaceEntityContactsOptions([]);
      setErrorMessage("Error loading contacts for the replacement entity.");
    }
  }

  function handleFindEntityContactChange(option, entityContactId) {
    setFindEntityContactsOptions((currentContacts) => currentContacts.map((contact) => {
      if (contact.value !== entityContactId) {
        return contact;
      }

      return {
        ...contact,
        map_to_entity_contact_id: option ? option.value : null,
        map_to_contact: option ? option.label : null,
      };
    }));
  }

  function handleEntitySearchKeyDown(event, onSearch) {
    if (event.key === "Enter") {
      event.preventDefault();
      onSearch();
    }
  }

  async function handleFindAndReplace() {
    clearMessages();

    if (!canSubmit) {
      setErrorMessage("Select different find and replace entities before continuing.");
      return;
    }

    const confirmed = window.confirm("The find/replace process is one way and cannot be undone. Are you sure you want to proceed?");
    if (!confirmed) {
      return;
    }

    const body = {
      selected_scopes: selectedScopes.map((scope) => scope.value),
      selected_find_entity: selectedFindEntity.value,
      selected_replace_entity: selectedReplaceEntity.value,
      contact_mapping: findEntityContactsOptions.map((contact) => ({
        from_entity_contact_id: contact.value,
        to_entity_contact_id: contact.map_to_entity_contact_id,
      })),
      sales: salesOption,
      sale_sheets: saleSheetsOption,
      managing_entity: managingAgentOption,
      leasing_entity: leasingAgentOption,
      lift_operators: liftOperatorsOption,
    };

    setIsSubmitting(true);

    try {
      const result = await executeFindAndReplace(body);

      if (result?.result === false) {
        setErrorMessage(result.error_message || "The find and replace request failed.");
        return;
      }

      setSuccessMessage("Replace was successful. Close the Find & Replace popup to continue.");
    } catch (error) {
      console.error("Error performing entity find and replace", error);
      setErrorMessage("The find and replace request failed.");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="EntityList" id={createId("container")}>
      {isAuthenticated ? (
        <div className="entities">
          {(errorMessage || successMessage) && (
            <Row className="mb-3">
              <Col>
                {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
                {!errorMessage && successMessage && <Alert variant="success">{successMessage}</Alert>}
              </Col>
            </Row>
          )}

          <Row>
            <Col xs={12} sm={12} md={4} lg={3} className="mb-3 mb-md-0">
              <div className="d-flex flex-column gap-2">
                <Form.Check
                  checked={salesOption}
                  onChange={(event) => setSalesOption(event.target.checked)}
                  label="Sales"
                  type="checkbox"
                  name="salesOption"
                  disabled
                  className="entity-find-replace-checkbox align-items-start"
                  inputClassName="entity-find-replace-checkbox-input"
                  style={{ marginLeft: '0' }}
                />

                <Form.Check
                  checked={saleSheetsOption}
                  onChange={(event) => setSaleSheetsOption(event.target.checked)}
                  label="Sale Sheets (current only)"
                  type="checkbox"
                  name="saleSheetsOption"
                  className="entity-find-replace-checkbox align-items-start"
                  inputClassName="entity-find-replace-checkbox-input"
                  style={{ marginLeft: '0' }}
                />

                <Form.Check
                  checked={managingAgentOption}
                  onChange={(event) => setManagingAgentOption(event.target.checked)}
                  label="Managing Agents"
                  type="checkbox"
                  name="managingAgentOption"
                  className="entity-find-replace-checkbox align-items-start"
                  inputClassName="entity-find-replace-checkbox-input"
                  style={{ marginLeft: '0' }}
                />

                <Form.Check
                  checked={leasingAgentOption}
                  onChange={(event) => setLeasingAgentOption(event.target.checked)}
                  label="Leasing Agents"
                  type="checkbox"
                  name="leasingAgentOption"
                  className="entity-find-replace-checkbox align-items-start"
                  inputClassName="entity-find-replace-checkbox-input"
                  style={{ marginLeft: '0' }}
                />

                <Form.Check
                  checked={liftOperatorsOption}
                  onChange={(event) => setLiftOperatorsOption(event.target.checked)}
                  label="Lift Operators"
                  type="checkbox"
                  name="liftOperatorsOption"
                  className="entity-find-replace-checkbox align-items-start"
                  inputClassName="entity-find-replace-checkbox-input"
                  style={{ marginLeft: '0' }}
                />
              </div>
            </Col>

            <Col xs={12} sm={12} md={8} lg={4} className="mb-3 mb-md-0">
              <Form.Group className="mb-3" controlId={createId("find-entity", "search-string")}>
                <Form.Label>Find Entity</Form.Label>
                <InputGroup size="sm">
                  <Form.Control
                    value={findEntitySearchString}
                    onChange={(event) => {
                      setFindEntitySearchString(event.target.value);
                      setShowFindEntityDropdown(false);
                    }}
                    onKeyDown={(event) => handleEntitySearchKeyDown(event, handleFindEntitySearch)}
                    type="text"
                    placeholder="Search entities"
                    disabled={isLoading || isSubmitting}
                  />
                  <Button
                    onClick={handleFindEntitySearch}
                    variant="outline-secondary"
                    size="sm"
                    type="button"
                    id={createId("find-entity", "search", "button")}
                    disabled={isLoading || isSubmitting}
                  >
                    Search
                  </Button>
                </InputGroup>
              </Form.Group>

              <Form.Group className="mb-3" controlId={createId("find-entity", "select")}>
                {showFindEntityDropdown && (
                  <Select
                    isClearable
                    value={selectedFindEntity}
                    onChange={handleFindEntityChange}
                    options={findEntityOptions}
                    isDisabled={isLoading || isSubmitting}
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      menu: (base) => ({ ...base, zIndex: 9999 })
                    }}
                  />
                )}
              </Form.Group>

              <div>
                <b>Contacts</b> - Ignore, copy across or map to replacement entity contact
                {selectedReplaceEntity && findEntityContactsOptions.length > 0 ? (
                  <Table responsive size="sm" className="mt-2">
                    <tbody>
                      {findEntityContactsOptions.map((contact) => (
                        <tr key={contact.value}>
                          <td>{contact.label}</td>
                          <td>
                            <Select
                              isClearable
                              value={contact.map_to_contact ? {
                                value: contact.map_to_entity_contact_id,
                                label: contact.map_to_contact,
                              } : null}
                              onChange={(option) => handleFindEntityContactChange(option, contact.value)}
                              options={replaceContactSelectOptions}
                              isDisabled={isLoading || isSubmitting}
                              menuPortalTarget={document.body}
                              styles={{
                                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                menu: (base) => ({ ...base, zIndex: 9999 })
                              }}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <div className="text-muted mt-2">
                    Select both entities to review contact mappings.
                  </div>
                )}
              </div>
            </Col>

            <Col xs={12} sm={12} md={8} lg={4} className="mb-3 mb-md-0">
              <Form.Group className="mb-3" controlId={createId("replace-entity", "search-string")}>
                <Form.Label>Replace Entity</Form.Label>
                <InputGroup size="sm">
                  <Form.Control
                    value={replaceEntitySearchString}
                    onChange={(event) => {
                      setReplaceEntitySearchString(event.target.value);
                      setShowReplaceEntityDropdown(false);
                    }}
                    onKeyDown={(event) => handleEntitySearchKeyDown(event, handleReplaceEntitySearch)}
                    type="text"
                    placeholder="Search entities"
                    disabled={isLoading || isSubmitting}
                  />
                  <Button
                    onClick={handleReplaceEntitySearch}
                    variant="outline-secondary"
                    size="sm"
                    type="button"
                    id={createId("replace-entity", "search", "button")}
                    disabled={isLoading || isSubmitting}
                  >
                    Search
                  </Button>
                </InputGroup>
              </Form.Group>

              <Form.Group className="mb-3" controlId={createId("replace-entity", "select")}>
                {showReplaceEntityDropdown && (
                  <Select
                    isClearable
                    value={selectedReplaceEntity}
                    onChange={handleReplaceEntityChange}
                    options={replaceEntityOptions}
                    isDisabled={isLoading || isSubmitting}
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      menu: (base) => ({ ...base, zIndex: 9999 })
                    }}
                  />
                )}
              </Form.Group>
            </Col>
          </Row>

          {selectedFindEntity && selectedReplaceEntity && selectedFindEntity.value === selectedReplaceEntity.value && (
            <Row className="mt-2">
              <Col>
                <Alert variant="warning" className="mb-0">
                  Find entity and replacement entity must be different.
                </Alert>
              </Col>
            </Row>
          )}

          <Row className="show-Container mt-3">
            <Col className="d-flex gap-2 justify-content-end">
              {onClose && (
                <Button variant="secondary" onClick={onClose} disabled={isSubmitting} id={createId("close", "button")}>
                  Close
                </Button>
              )}
              <Button
                type="button"
                variant="primary"
                onClick={handleFindAndReplace}
                id={createId("find-replace", "button")}
                disabled={!canSubmit || isLoading || isSubmitting}
              >
                {isSubmitting ? "Processing..." : "Perform Find/Replace"}
              </Button>
            </Col>
          </Row>
        </div>
      ) : null}
    </div>
  );
}

export default EntityPageFindAndReplace;